import { refreshAccessToken } from '../services/AuthService';
import { getCookie, removeCookie, setCookie } from './CookieUtil';

const AUTH_COOKIE_NAMES = ['accessToken', 'refreshToken'];

interface JwtPayload {
  exp?: number;
}

const decodeBase64Url = (value: string) => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return window.atob(padded);
};

const parseJwtPayload = (token: string): JwtPayload | null => {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(payload)) as JwtPayload;
  } catch (error) {
    return null;
  }
};

export const clearAuthCookies = () => {
  AUTH_COOKIE_NAMES.forEach(removeCookie);
};

export const isAccessTokenValid = (token: string | null) => {
  if (!token) {
    return false;
  }

  const payload = parseJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  return payload.exp * 1000 > Date.now();
};

export const hasValidAuthToken = () => {
  return isAccessTokenValid(getCookie('accessToken')) || isAccessTokenValid(getCookie('refreshToken'));
};

export const getValidAccessToken = () => {
  const token = getCookie('accessToken');
  if (isAccessTokenValid(token)) {
    return token;
  }

  clearAuthCookies();
  return null;
};

export const getValidAccessTokenOrRefresh = async () => {
  const accessToken = getCookie('accessToken');
  if (isAccessTokenValid(accessToken)) {
    return accessToken;
  }

  const refreshToken = getCookie('refreshToken');
  if (!refreshToken || !isAccessTokenValid(refreshToken)) {
    clearAuthCookies();
    return null;
  }

  try {
    const data = await refreshAccessToken(refreshToken);
    setCookie('accessToken', data.accessToken, 1);
    setCookie('refreshToken', data.refreshToken, 7);
    return data.accessToken as string;
  } catch (error) {
    clearAuthCookies();
    return null;
  }
};
